import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { differenceInMinutes, format, isValid, set } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useFormContext } from 'react-hook-form';

const DEFAULT_HOUR = 9;
const DEFAULT_MINUTE = 0;

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return isValid(date) ? date : null;
}

function mergeDateAndTime(datePart: Date | null, timePart: Date | null, fallbackDate?: Date | null): Date | null {
  const baseDate = datePart ?? fallbackDate;
  if (!baseDate || !timePart) return null;
  return set(baseDate, {
    hours: timePart.getHours(),
    minutes: timePart.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  });
}

function formatDuration(start: Date, end: Date): string {
  const totalMinutes = differenceInMinutes(end, start);
  if (totalMinutes <= 0) return 'Término deve ser depois do início';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
  return `${hours}h ${minutes}min`;
}

interface SplitDateTimeFieldProps {
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  minDate?: Date | null;
  minTime?: Date | null;
  error?: boolean;
  dateHelperText?: string;
  timeHelperText?: string;
}

const SplitDateTimeField: React.FC<SplitDateTimeFieldProps> = ({
  label,
  value,
  onChange,
  minDate,
  minTime,
  error,
  dateHelperText,
  timeHelperText,
}) => {
  const dateValue = value ? set(value, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }) : null;
  const timeValue =
    value ??
    set(new Date(), { hours: DEFAULT_HOUR, minutes: DEFAULT_MINUTE, seconds: 0, milliseconds: 0 });

  const handleDateChange = (nextDate: Date | null) => {
    if (!nextDate) {
      onChange(null);
      return;
    }
    const merged = mergeDateAndTime(nextDate, timeValue, nextDate);
    onChange(merged);
  };

  const handleTimeChange = (nextTime: Date | null) => {
    if (!nextTime) return;
    const merged = mergeDateAndTime(dateValue ?? new Date(), nextTime, new Date());
    onChange(merged);
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={7}>
          <DatePicker
            label="Data"
            value={dateValue}
            onChange={handleDateChange}
            minDate={minDate ?? undefined}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                fullWidth: true,
                error,
                helperText: dateHelperText,
                InputLabelProps: { shrink: true },
              },
              actionBar: { actions: ['cancel', 'accept'] },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TimePicker
            label="Hora"
            value={timeValue}
            onChange={handleTimeChange}
            ampm={false}
            minutesStep={1}
            minTime={minTime ?? undefined}
            format="HH:mm"
            slotProps={{
              textField: {
                fullWidth: true,
                error,
                helperText: timeHelperText || 'Formato 24h',
                InputLabelProps: { shrink: true },
              },
              actionBar: { actions: ['cancel', 'accept'] },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const EventScheduleFields: React.FC = () => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const startDate = parseDate(watch('startDate'));
  const endDate = parseDate(watch('endDate'));

  const durationLabel =
    startDate && endDate && endDate > startDate
      ? formatDuration(startDate, endDate)
      : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Quando acontece?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Escolha a data no calendário e ajuste a hora em minutos — sem intervalos de 5 em 5.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SplitDateTimeField
              label="Início *"
              value={startDate}
              onChange={(date) => setValue('startDate', date, { shouldDirty: true, shouldValidate: true })}
              minDate={new Date()}
              error={!!errors.startDate}
              dateHelperText={errors.startDate?.message as string}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <SplitDateTimeField
              label="Término *"
              value={endDate}
              onChange={(date) => setValue('endDate', date, { shouldDirty: true, shouldValidate: true })}
              minDate={startDate ?? new Date()}
              minTime={
                startDate && endDate && endDate.toDateString() === startDate.toDateString()
                  ? startDate
                  : undefined
              }
              error={!!errors.endDate}
              dateHelperText={errors.endDate?.message as string}
            />
          </Grid>
        </Grid>

        {durationLabel && startDate && endDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Duração: <strong>{durationLabel}</strong> — {format(startDate, "dd/MM 'às' HH:mm", { locale: ptBR })} até{' '}
            {format(endDate, "dd/MM 'às' HH:mm", { locale: ptBR })}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default EventScheduleFields;
